import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Trash2, X } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { FREE_SHIPPING_THRESHOLD, getAmountUntilFreeShipping, getShippingCost } from '../utils/shipping';

const CartDrawer = () => {
  const {
    items,
    removeFromCart,
    updateQuantity,
    getCartTotal,
    getCartCount,
    isCartOpen,
    closeCart,
  } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = isCartOpen ? 'hidden' : '';

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        closeCart();
      }
    };

    if (isCartOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isCartOpen, closeCart]);

  const formatPrice = (price) => {
    if (typeof price === 'string') {
      return price;
    }

    return `Rs. ${(price || 0).toLocaleString()}`;
  };

  const getNumericPrice = (price) => {
    if (typeof price === 'string') {
      return parseInt(price.replace(/[^0-9]/g, ''), 10) || 0;
    }

    return price || 0;
  };

  const handleQuantityChange = (item, change) => {
    const nextQuantity = item.quantity + change;

    if (nextQuantity < 1) {
      removeFromCart(item.id);
      return;
    }

    if (item.maxStock && nextQuantity > item.maxStock) {
      return;
    }

    updateQuantity(item.id, nextQuantity);
  };

  const subtotal = getCartTotal();
  const shippingCost = getShippingCost(subtotal);
  const amountUntilFreeShipping = getAmountUntilFreeShipping(subtotal);
  const freeShippingProgress = Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const total = subtotal + shippingCost;

  return (
    <div
      className={`fixed inset-0 z-[80] transition ${
        isCartOpen ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
      aria-hidden={!isCartOpen}
    >
      <button
        type="button"
        aria-label="Close cart drawer"
        onClick={closeCart}
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isCartOpen ? 'opacity-50' : 'opacity-0'
        }`}
      />

      <aside
        className={`absolute right-0 top-0 flex h-full w-full max-w-md transform flex-col bg-white shadow-2xl transition-transform duration-300 ${
          isCartOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-label="Shopping cart"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              Mughal Ainak Mahal
            </p>
            <h2 className="text-xl font-bold text-secondary">Your Cart</h2>
          </div>
          <button
            type="button"
            onClick={closeCart}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 hover:text-primary transition-colors duration-200"
            aria-label="Close cart"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center px-8 text-center">
            <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
              <ShoppingBag className="h-10 w-10 text-gray-400" />
            </div>
            <h3 className="mb-2 text-2xl font-bold text-secondary">Your cart is empty</h3>
            <p className="mb-8 text-sm leading-6 text-gray-600">
              Add your favorite frames, sunglasses, or lenses and they will appear here.
            </p>
            <button
              type="button"
              onClick={closeCart}
              className="w-full rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-dark transition-colors duration-200"
            >
              Continue Shopping
            </button>
          </div>
        ) : (
          <>
            <div className="border-b border-gray-200 bg-gray-50 px-5 py-4">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-secondary">
                  {shippingCost === 0 ? 'Free delivery unlocked' : 'Standard delivery'}
                </span>
                <span className="font-semibold text-primary">
                  {shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-primary transition-all duration-300"
                  style={{ width: `${freeShippingProgress}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-gray-600">
                {shippingCost === 0
                  ? 'You qualified for free delivery.'
                  : `Spend ${formatPrice(amountUntilFreeShipping)} more for free delivery.`}
              </p>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-2">
              {items.map((item) => {
                const itemPrice = getNumericPrice(item.price);

                return (
                  <div key={item.id} className="flex gap-4 border-b border-gray-200 py-4 last:border-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-20 w-20 flex-shrink-0 rounded-lg object-cover bg-gray-100"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-sm font-semibold leading-5 text-secondary">
                            {item.name}
                          </h3>
                          <p className="mt-1 text-sm font-bold text-primary">
                            {formatPrice(item.price)}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFromCart(item.id)}
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors duration-200"
                          aria-label={`Remove ${item.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <div className="flex items-center rounded-lg border border-gray-300">
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(item, -1)}
                            className="flex h-9 w-9 items-center justify-center text-gray-700 hover:text-primary transition-colors duration-200"
                            aria-label={`Decrease ${item.name} quantity`}
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-10 text-center text-sm font-semibold text-secondary">
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(item, 1)}
                            className="flex h-9 w-9 items-center justify-center text-gray-700 hover:text-primary transition-colors duration-200"
                            aria-label={`Increase ${item.name} quantity`}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <span className="text-sm font-bold text-secondary">
                          {formatPrice(itemPrice * item.quantity)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-gray-200 px-5 py-5">
              <div className="mb-4 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Items</span>
                  <span className="font-semibold text-secondary">{getCartCount()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold text-secondary">{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Delivery</span>
                  <span className="font-semibold text-secondary">
                    {shippingCost === 0 ? 'Free' : formatPrice(shippingCost)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-200 pt-3 text-lg font-bold">
                  <span className="text-secondary">Total</span>
                  <span className="text-primary">{formatPrice(total)}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  closeCart();
                  navigate('/checkout');
                }}
                className="mb-3 w-full rounded-lg bg-primary px-6 py-3 font-semibold text-white hover:bg-primary-dark transition-colors duration-200"
              >
                Proceed to Checkout
              </button>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={closeCart}
                  className="rounded-lg bg-gray-100 px-4 py-3 text-sm font-semibold text-secondary hover:bg-gray-200 transition-colors duration-200"
                >
                  Continue Shopping
                </button>
                <Link
                  to="/cart"
                  onClick={closeCart}
                  className="rounded-lg border border-gray-300 px-4 py-3 text-center text-sm font-semibold text-secondary hover:border-primary hover:text-primary transition-colors duration-200"
                >
                  View Cart
                </Link>
              </div>
            </div>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
